import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, BookOpenCheck, FileText, Printer } from "lucide-react";
import { toast } from "sonner";

const SUBJECTS = ["English", "Mathematics", "Arabic", "Islamic Studies", "Science", "Social Studies", "ICT"];

const Exams = () => {
  const { user } = useAuth();
  const effectiveCapabilities = user?.effective_capabilities || [];
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isSuperAdmin = userRoles.includes("super_admin");
  const hasEffectiveCapability = (capability) =>
    isSuperAdmin || effectiveCapabilities.includes(capability);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [examOpen, setExamOpen] = useState(false);
  const [marksOpen, setMarksOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [exam, setExam] = useState({ title: "", grade: "Primary 5", term: "Term 1 2026/2027", start_date: "", end_date: "" });
  const [selectedExam, setSelectedExam] = useState(null);
  const [marks, setMarks] = useState({});  // { studentId: { subject: score } }
  const [report, setReport] = useState(null);

  const canConfigure =
    hasEffectiveCapability("assessment.configure");
  const canEnterMarks =
    hasEffectiveCapability("assessment.enter_marks");

  const load = async () => {
    try {
      const [ex, st] = await Promise.all([api.get("/exams"), api.get("/students")]);
      setExams(ex.data); setStudents(st.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const createExam = async () => {
    try {
      const { data } = await api.post("/exams", exam);
      setExams((l) => [data, ...l]); setExamOpen(false);
      setExam({ title: "", grade: "Grade 5", term: "Term 1 2026", start_date: "", end_date: "" });
      toast.success("Exam created");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openMarks = (ex) => { setSelectedExam(ex); setMarks({}); setMarksOpen(true); };

  const saveMarks = async () => {
    const entries = [];
    Object.entries(marks).forEach(([sid, subj]) => {
      Object.entries(subj).forEach(([subject, score]) => {
        if (score !== "" && score != null) entries.push({ student_id: sid, subject, score: Number(score), max_score: 100 });
      });
    });
    if (entries.length === 0) return toast.error("Enter at least one score");
    try {
      await api.post("/exams/marks", { exam_id: selectedExam.id, entries });
      toast.success(`Recorded ${entries.length} marks`); setMarksOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewReport = async (student, ex) => {
    try {
      const { data } = await api.get(`/students/${student.id}/report-card/${ex.id}`);
      setReport(data); setReportOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const print = () => window.print();

  const gradeStudents = selectedExam ? students.filter(s => s.grade === selectedExam.grade) : [];

  return (
    <div data-testid="exams-page">
      <PageHeader
        overline="Academics"
        title="Examinations & Report Cards"
        description="Create exam sessions, record subject-wise marks and generate report cards."
        actions={canConfigure && (
          <Dialog open={examOpen} onOpenChange={setExamOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="new-exam-btn"><Plus className="w-4 h-4 mr-1.5" /> New Exam</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Create Exam Session</DialogTitle></DialogHeader>
              <div className="grid gap-4 mt-2">
                <Fld label="Title"><Input value={exam.title} onChange={(e) => setExam({ ...exam, title: e.target.value })} placeholder="Mid-Term 2026" data-testid="exam-title" /></Fld>
                <Fld label="Grade">
                  <Select value={exam.grade} onValueChange={(v) => setExam({ ...exam, grade: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Primary 1","Primary 2","Primary 3","Primary 4","Primary 5","Primary 6","JSS 1","JSS 2","JSS 3","SS 1","SS 2","SS 3"].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Fld>
                <Fld label="Term"><Input value={exam.term} onChange={(e) => setExam({ ...exam, term: e.target.value })} /></Fld>
                <div className="grid grid-cols-2 gap-4">
                  <Fld label="Start"><Input type="date" value={exam.start_date} onChange={(e) => setExam({ ...exam, start_date: e.target.value })} /></Fld>
                  <Fld label="End"><Input type="date" value={exam.end_date} onChange={(e) => setExam({ ...exam, end_date: e.target.value })} /></Fld>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setExamOpen(false)}>Cancel</Button>
                <Button onClick={createExam} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="exam-submit">Create Exam</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {exams.length === 0 ? <EmptyState message="No exams yet." testId="exams-empty" /> : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-[#FDFBF7]">
              <TableHead>Title</TableHead><TableHead>Grade</TableHead><TableHead>Term</TableHead><TableHead>Dates</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {exams.map(ex => (
                <TableRow key={ex.id} data-testid={`exam-row-${ex.id}`}>
                  <TableCell className="font-medium text-[#1B2C5A]">{ex.title}</TableCell>
                  <TableCell>{ex.grade}</TableCell>
                  <TableCell>{ex.term}</TableCell>
                  <TableCell className="text-xs">{ex.start_date || "—"} → {ex.end_date || "—"}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {canEnterMarks && <Button size="sm" variant="outline" onClick={() => openMarks(ex)} data-testid={`exam-marks-${ex.id}`}><BookOpenCheck className="w-4 h-4 mr-1" /> Marks</Button>}
                    <Select onValueChange={(sid) => { const stu = students.find(s => s.id === sid); if (stu) viewReport(stu, ex); }}>
                      <SelectTrigger className="w-40 inline-flex h-9"><SelectValue placeholder="Report Card" /></SelectTrigger>
                      <SelectContent>
                        {students.filter(s => s.grade === ex.grade).map(s => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Marks entry */}
      <Dialog open={marksOpen} onOpenChange={setMarksOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Record Marks — {selectedExam?.title} · {selectedExam?.grade}</DialogTitle></DialogHeader>
          {gradeStudents.length === 0 ? <div className="text-sm text-[#475569] py-6">No students in this grade.</div> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Student</TableHead>{SUBJECTS.map(s => <TableHead key={s}>{s}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {gradeStudents.map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium text-[#1B2C5A]">{s.full_name}</TableCell>
                      {SUBJECTS.map(sub => (
                        <TableCell key={sub}>
                          <Input
                            type="number" min="0" max="100"
                            className="w-20 h-9"
                            value={marks[s.id]?.[sub] ?? ""}
                            onChange={(e) => setMarks(m => ({ ...m, [s.id]: { ...(m[s.id] || {}), [sub]: e.target.value } }))}
                            data-testid={`mark-${s.id}-${sub}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarksOpen(false)}>Cancel</Button>
            <Button onClick={saveMarks} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="save-marks">Save Marks</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report card */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Report Card</DialogTitle></DialogHeader>
          {report && (
            <div id="report-card" className="p-6 border border-[#E2E8F0] rounded-xl bg-white">
              <div className="overline text-[#9DCD3F]">Darussalam Foundation Academy</div>
              <div className="flex items-end justify-between mt-2">
                <div>
                  <h2 className="font-serif text-3xl text-[#1B2C5A]">{report.student.full_name}</h2>
                  <div className="text-sm text-[#475569]">{report.student.grade} · Adm. {report.student.admission_no}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#475569]">{report.exam.title}</div>
                  <div className="text-xs text-[#475569]">{report.exam.term}</div>
                </div>
              </div>
              <table className="w-full mt-6 text-sm">
                <thead><tr className="text-left text-[#475569] border-b border-[#E2E8F0]"><th className="py-2">Subject</th><th>Score</th><th>Max</th><th>Grade</th></tr></thead>
                <tbody>
                  {report.marks.map((m, i) => (
                    <tr key={i} className="border-b border-[#E2E8F0]/70">
                      <td className="py-2 text-[#1B2C5A] font-medium">{m.subject}</td>
                      <td>{m.score}</td><td>{m.max_score}</td>
                      <td>{m.score >= 80 ? "A" : m.score >= 65 ? "B" : m.score >= 50 ? "C" : "D"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div><div className="overline text-[#9DCD3F]">Total</div><div className="font-display text-2xl text-[#1B2C5A]">{report.total}/{report.max_total}</div></div>
                <div><div className="overline text-[#9DCD3F]">Percentage</div><div className="font-display text-2xl text-[#1B2C5A]">{report.percentage}%</div></div>
                <div><div className="overline text-[#9DCD3F]">Overall</div><div className="font-display text-2xl text-[#9DCD3F]">{report.overall_grade}</div></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={print} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="print-report"><Printer className="w-4 h-4 mr-2" /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default Exams;
